import React from 'react';
import { useRecoilState } from 'recoil';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { ReactComponent as EditIcon } from 'assets/images/edit.svg';
import { Box, Button, CircularProgress, Table, TableBody, TableContainer, TableHead, Typography } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import axios, { endPoints } from 'api/request';
import { format } from 'date-fns';
import Switch from 'components/switch';
import Empty from 'components/empty';
import Modal from 'components/modal';
import toast from 'react-hot-toast';
import { handleReset } from 'pages/user-preferences/utils/getPayload';
import { OrderPayload, toggleCreateRule, toggleDeleteRule, ruleListState } from 'pages/user-preferences/store';
import style from 'pages/user-preferences/style';
// import DelegateNavigation from 'pages/user-preferences/components/delegates/DelegateNavigation';
import { ConfirmDeleteModal } from './confirmDeleteModal';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme.palette.blue.strongDark,
        font: theme.typography.fontSize.$font_small_bold,
        padding: '15px',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        whiteSpace: 'nowrap',
    },
    [`&.${tableCellClasses.body}`]: {
        font: theme.typography.fontSize.$font_small_regular,
        color: theme.palette.cmpBlack.doply,
        padding: '15px',
    },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: '#ffffff',
    boxShadow: 'inset 0 -2px #d7e5f8',
    '&:hover': {
        backgroundColor: theme.palette.cyan['90'],
    },
}));

export const RulesDefaultList = () => {
    const history = useHistory();
    const [, setOrder] = useRecoilState(OrderPayload);
    const [ruleList, setRuleList] = useRecoilState(ruleListState);
    const [seldActiveIds, setSeldActiveIds] = React.useState({});
    const [showTableBody, setShowTableBody] = React.useState(false);
    const [confirmModal, setConfirmModal] = useRecoilState(toggleDeleteRule);
    const [modal, setModal] = useRecoilState(toggleCreateRule);
    const [ruleTypeModal, setRuleTypeModal] = React.useState({ isOpen: false, rule: null });
    const fetchRuleListApi = useMutation(() => axios.get(endPoints?.staticLookups?.autoApprovalRules));

    const updateRuleApi = useMutation((payload) => axios.put(`${endPoints?.staticLookups?.autoApprovalRule}`, payload), {
        onSuccess: (data, variables) => {
            if (data?.isSuccess) {
                const updatedRule = ruleList?.map((rule) => (rule?.id === variables?.id ? { ...rule, active: variables?.active } : rule));
                setRuleList(updatedRule);
                toast.success(`${data?.messages?.[0]}`);
            }
        },
    });

    React.useEffect(() => {
        fetchRuleListApi?.mutate();
    }, []);

    React.useEffect(() => {
        if (fetchRuleListApi?.data) {
            setRuleList(fetchRuleListApi?.data?.data?.preApprovalRules);
            setShowTableBody(fetchRuleListApi?.data?.data?.preApprovalRules?.length > 0);
        }
    }, [fetchRuleListApi?.data]);

    const handleSwitchChange = (index) => {
        const selectedRule = ruleList[index];
        setSeldActiveIds(selectedRule);
        const payload = {
            id: selectedRule?.id,
            name: selectedRule?.name,
            criteria: {
                ...(selectedRule?.criteria?.requestors && { requestors: selectedRule?.criteria?.requestors }),
                ...(selectedRule?.criteria?.catalogServices && { catalogServices: selectedRule?.criteria?.catalogServices }),
                ...(selectedRule?.criteria?.maxOrderAmount && { maxOrderAmount: selectedRule?.criteria?.maxOrderAmount }),
            },
            budgetCode: selectedRule?.budgetCode,
            active: !selectedRule?.active,
        };
        updateRuleApi.mutate(payload);
    };

    const handleDelete = (id) => {
        setConfirmModal({
            ...confirmModal,
            type: 'delete',
            isOpen: true,
            message: 'Do you want to delete the Rule?',
            id,
        });
    };

    const resetOrder = () => {
        setOrder(handleReset());
    };

    const handleRowClick = (rule) => {
        setRuleTypeModal({ isOpen: true, rule });
    };

    const handleCloseModal = () => {
        setRuleTypeModal({ isOpen: false, rule: null });
    };

    React.useEffect(() => {
        const rwcElement = document.getElementById('rwc');
        if (rwcElement) {
            rwcElement.style.display = ruleTypeModal?.isOpen ? 'none' : 'block';
        }
    }, [ruleTypeModal?.isOpen]);

    const ruleTypeData = (criteria) => {
        if (!criteria) return null;
        return (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Rule Type</StyledTableCell>
                            <StyledTableCell>Rule Value</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object?.keys(criteria)?.map((key) => {
                            const value = criteria[key];
                            if (key === 'maxOrderAmount' || value === null || value === undefined || (Array.isArray(value) && value?.length === 0)) {
                                return null;
                            }
                            const formattedKey = key?.replace(/([A-Z])/g, ' $1')?.replace(/^./, (str) => str?.toUpperCase());
                            const formattedValue = Array?.isArray(value)
                                ? value?.map((item) => item?.replace(/_/g, ' ').replace(/\b\w/g, (char) => char?.toUpperCase()))
                                : [value];
                            return (
                                <StyledTableRow key={key}>
                                    <StyledTableCell>{formattedKey}</StyledTableCell>
                                    <StyledTableCell>
                                        <ul>
                                            {formattedValue?.map((val, index) => (
                                                <li key={`${key}-${index}`}>{val}</li>
                                            ))}
                                        </ul>
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Box className="contentContainer">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography sx={{ fontSize: '22px', paddingTop: '30px', fontWeight: 600 }}>Auto Approval Rules</Typography>
                    <Typography sx={{ fontSize: '16px', color: '#616161' }}>You can define rules to set up an auto approval process</Typography>
                </Box>
                <Box>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                            resetOrder();
                            history.push('/user-preferences/create');
                        }}
                        fullWidth
                        sx={style.createApprovalButton}
                        data-testid="create-rule-btn"
                        disableRipple
                        startIcon={<AddIcon />}
                    >
                        Create Rule
                    </Button>
                </Box>
            </Box>
            <Box overflow="auto" display="flex" flexDirection="column" pt={1.7} pb={1} sx={{ color: '#ffffff', justifyContent: 'stretch' }}>
                <TableContainer sx={{ marginTop: '4px' }}>
                    <Table sx={{ marginTop: '-9px', borderSpacing: '0 4px', minWidth: 500, height: 'auto' }} size="small" stickyHeader>
                        <TableHead key="head">
                            <TableRow sx={{ height: '36px' }}>
                                <StyledTableCell key="head-switch" width="5%" />
                                <StyledTableCell width="55%">RULE NAME</StyledTableCell>
                                <StyledTableCell width="10%">CREATED ON</StyledTableCell>
                                <StyledTableCell width="10%">UPDATED ON</StyledTableCell>
                                <StyledTableCell align="center" width="20%">
                                    ACTIONS
                                </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        {fetchRuleListApi?.isLoading && (
                            <TableBody>
                                <StyledTableRow sx={style?.list?.emptyRow?.row}>
                                    <StyledTableCell sx={style?.list?.emptyRow?.cell} colSpan="5">
                                        <Empty isLoading={fetchRuleListApi?.isLoading} msg="Loading..." color="#000000" />
                                    </StyledTableCell>
                                </StyledTableRow>
                            </TableBody>
                        )}
                        {!fetchRuleListApi?.isLoading && showTableBody && (
                            <TableBody>
                                {ruleList?.map((result, index) => (
                                    <StyledTableRow sx={{ height: '36px' }} className="animate-opacity" key={`row-${index}`}>
                                        <StyledTableCell key={`switch-${index}`} width="5%">
                                            {seldActiveIds?.id === result?.id && updateRuleApi?.isLoading ? (
                                                <CircularProgress size={16} sx={style?.list?.circularIcon} data-testid="switch-loader" />
                                            ) : (
                                                <Switch data-testid={`switch-${index}`} checked={result?.active} onChange={() => handleSwitchChange(index)} />
                                            )}
                                        </StyledTableCell>
                                        <StyledTableCell width="55%">
                                            <Box
                                                style={{ cursor: 'pointer', fontWeight: 'normal', fontSize: '14px' }}
                                                data-testid={`row-name-${index}`}
                                                onClick={() => handleRowClick(result)}
                                            >
                                                {result?.name}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell width="10%">{format(new Date(result?.createdTimestamp), 'dd/MM/yyyy')}</StyledTableCell>
                                        <StyledTableCell width="10%">{format(new Date(result?.updatedTimestamp), 'dd/MM/yyyy')}</StyledTableCell>
                                        <StyledTableCell width="20%">
                                            <Box display="flex" justifyContent="center">
                                                <Box
                                                    sx={{
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '27px',
                                                        height: '27px',
                                                        background: '#0047BA',
                                                        marginRight: '8px',
                                                        cursor: result?.active ? 'pointer' : 'default',
                                                        opacity: result?.active ? 1 : 0.5,
                                                    }}
                                                >
                                                    <EditIcon
                                                        style={{
                                                            width: '14px',
                                                            height: '12px',
                                                            color: '#FFFFFF',
                                                            cursor: result?.active ? 'pointer' : 'default',
                                                        }}
                                                        onClick={() => {
                                                            if (result?.active) {
                                                                setModal({ ...modal, type: 'edit' });
                                                                history.push({
                                                                    pathname: `/user-preferences/edit/${result?.id}`,
                                                                });
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                                <Box
                                                    sx={{
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '27px',
                                                        height: '27px',
                                                        background: '#DB1E36',
                                                        cursor: result?.active ? 'pointer' : 'default',
                                                        opacity: result?.active ? 1 : 0.5,
                                                    }}
                                                    onClick={() => {
                                                        if (result?.active) {
                                                            handleDelete(result?.id);
                                                        }
                                                    }}
                                                    data-testid={`rule-delete-icon-${index}`}
                                                >
                                                    <DeleteIcon
                                                        style={{
                                                            fontSize: '18px',
                                                            color: '#FFFFFF',
                                                            cursor: result?.active ? 'pointer' : 'default',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>

                {!fetchRuleListApi?.isLoading && (!ruleList || !ruleList?.length) && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        mt={2}
                        whiteSpace="nowrap"
                        color="#000000"
                        fontSize="14px"
                        fontWeight={500}
                        data-testid="create-approve-rule-text"
                    >
                        Create your Approval Rule
                    </Box>
                )}
            </Box>
            {/* <DelegateNavigation /> */}
            <ConfirmDeleteModal setRuleList={setRuleList} />
            <Modal open={ruleTypeModal?.isOpen} handleClose={handleCloseModal} title="Rule Type Details" data-testid="create-rule-model">
                {ruleTypeModal?.rule && (
                    <Box mt={2}>
                        <Typography>
                            <strong>Budget Code:</strong> {ruleTypeModal?.rule?.budgetCode}
                        </Typography>
                        <Typography>
                            <strong>Rule Name:</strong> {ruleTypeModal?.rule?.name}
                        </Typography>
                        {ruleTypeData(ruleTypeModal?.rule?.criteria)}
                    </Box>
                )}
            </Modal>
        </Box>
    );
};

export default React.memo(RulesDefaultList);
